import { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { searchCommissions } from '../../../Module/Settlement/Presentation/settlementActions';
import {
  selectCommissionSearchStatus,
  selectCommissionSearchResults,
  selectCommissionSearchMeta
} from '../../../Module/Settlement/Presentation/settlementSelectors';
import { clearCommissionSearch } from '../../../Module/Settlement/Presentation/settlementSlice';
import defaultProfileIcon from '../../../assets/Icon/project/default-profile.png';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import type { 
  CommissionSearchItem, 
  CommissionSearchMeta 
} from '../../../Module/Settlement/Domain/entity/SettlementTypes';

export type DonorSummary = {
  total: number;       // 총 후원 신청자
  cancelled: number;   // 후원 해지자
  failed: number;      // 후원 실패자
  inProgress: number;  // 후원 진행자
};

export type DonorPaymentMethod = 'cms' | 'card';
export type DonorStatusCode = 'complete' | 'fail' | 'cancel';

export type DonorItem = {
  id: string;
  name: string;
  method: DonorPaymentMethod;
  status: DonorStatusCode;
  email: string;
  phone: string;
  date: string;
  message: string;
  address: string;
  donationType: string;
  amount: number;
  count: number;
  profileImg: string;
};

// pay_type을 한글로 변환
const getPaymentMethod = (payType: string): DonorPaymentMethod => {
  const lowerType = payType?.toLowerCase() ?? '';
  if (lowerType.includes('card') || lowerType.includes('thebill')) {
    return 'card';
  }
  return 'cms';
};

// 결제 상태 결정
const getPaymentStatus = (item: CommissionSearchItem): DonorStatusCode => {
  if (item.real_amount > 0) {
    return 'complete';
  }
  // 추후 취소 상태 판별 로직 추가 가능
  return 'fail';
};

const formatDisplayDate = (rawDate?: string) => {
  if (!rawDate) return '';
  const dateObj = new Date(rawDate);
  if (Number.isNaN(dateObj.getTime())) return '';
  return `${dateObj.getFullYear()}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${String(dateObj.getDate()).padStart(2, '0')}`;
};

const normalizeDatePart = (value?: string | number) => {
  if (value === undefined || value === null) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  return stringValue.padStart(2, '0');
};

const formatStartDateFromParts = (item: CommissionSearchItem) => {
  const year = String(item.sponsor_start_year ?? '').trim();
  const month = normalizeDatePart(item.sponsor_start_month);
  const day = normalizeDatePart(item.sponsor_start_day);
  if (!year || !month || !day) return '';
  return `${year}.${month}.${day}`;
};

export const useDonorManagement = (targetProjectId?: string) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();

  // Project 정보 가져오기
  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  
  // 전달받은 ID가 있으면 그것을, 없으면 선택된 프로젝트나 첫 번째 프로젝트 사용
  const projectId = useMemo(() => {
    if (targetProjectId) return targetProjectId;
    
    if (selectedProject?.project_id) {
      return selectedProject.project_id;
    }
    if (projects.length > 0 && projects[0].project_id) {
      return projects[0].project_id;
    }
    return null;
  }, [targetProjectId, selectedProject, projects]);

  const currentProject = useMemo(() => {
    if (!projectId) return undefined;
    return projects.find(p => p.project_id === projectId || p._id === projectId);
  }, [projectId, projects]);

  // Settlement API 상태 (타입 명시)
  const searchStatus = useAppSelector(selectCommissionSearchStatus);
  const searchResults = useAppSelector(selectCommissionSearchResults) as CommissionSearchItem[] | undefined;
  const searchMeta = useAppSelector(selectCommissionSearchMeta) as CommissionSearchMeta | undefined;

  // 후원자 데이터 가져오기
  const fetchDonors = useCallback(() => {
    if (!projectId) return;
    
    dispatch(searchCommissions({
      params: [
        { fld: 'projectId', val: projectId, op: 'eq' }
      ],
      local_field: 'projectId',
      from: 'projects',
      sort: 'created_at:desc',
      limit: 100
    }));
  }, [dispatch, projectId]);

  // 프로젝트 ID 변경 시 데이터 새로 가져오기
  useEffect(() => {
    if (projectId) {
      fetchDonors();
    }
  }, [projectId, fetchDonors]);

  // 정리
  useEffect(() => {
    return () => {
      dispatch(clearCommissionSearch());
    };
  }, [dispatch]);

  // 후원자별로 그룹화하여 데이터 변환
  const donors: DonorItem[] = useMemo(() => {
    if (!searchResults || searchResults.length === 0) {
      return [];
    }

    // 후원자 이름으로 그룹화
    const donorMap = new Map<string, {
      items: CommissionSearchItem[];
      totalCount: number;
    }>();

    searchResults.forEach(item => {
      const name = item.sponsor_name ?? t('common.labels.anonymous');
      if (!donorMap.has(name)) {
        donorMap.set(name, { items: [], totalCount: 0 });
      }
      const donor = donorMap.get(name)!;
      donor.items.push(item);
      donor.totalCount += 1;
    });

    // 그룹화된 데이터를 DonorItem 배열로 변환
    return Array.from(donorMap.entries()).map(([name, data], index) => {
      const latestItem = data.items[0]; // 가장 최근 결제 정보
      
      // 등록일은 sponsor_start_year/month/day 조합을 우선 사용
      const formattedDate =
        formatStartDateFromParts(latestItem) ||
        formatDisplayDate(latestItem.sponsor_start_date) ||
        formatDisplayDate(latestItem.pay_date);

      const isOneTime = ['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes((currentProject?.type ?? '').toUpperCase());
      const donationType = isOneTime ? t('page.newProject.review.fundingType.once') : t('page.newProject.review.fundingType.regular');

      return {
        id: latestItem.mgmt_commission_id ?? `donor-${index}`,
        name,
        method: getPaymentMethod(latestItem.pay_type),
        status: getPaymentStatus(latestItem),
        email: latestItem.email ?? '',
        phone: latestItem.sponsor_tel ?? '',
        date: formattedDate,
        message: latestItem.comment ?? '',
        address: '',
        donationType,
        amount: latestItem.sponsor_amount ?? 0,
        count: data.totalCount,
        profileImg: defaultProfileIcon
      };
    });
  }, [searchResults, t, currentProject]);

  // 요약 데이터 계산
  const summary: DonorSummary = useMemo(() => {
    const total = donors.length;
    const cancelled = donors.filter(d => d.status === 'cancel').length;
    const failed = donors.filter(d => d.status === 'fail').length;
    const inProgress = donors.filter(d => d.status === 'complete').length;

    return {
      total,
      cancelled,
      failed,
      inProgress
    };
  }, [donors]);

  // 데이터 존재 여부
  const hasData = donors.length > 0;

  // 로딩 상태
  const isLoading = searchStatus === 'searching';

  // 페이지네이션 정보
  const pagination = useMemo(() => ({
    totalElements: searchMeta?.totalElements ?? 0,
    totalPages: searchMeta?.totalPages ?? 0,
    currentPage: Math.floor((searchMeta?.msearch?.offset ?? 0) / (searchMeta?.perPage ?? 10)) + 1,
    perPage: searchMeta?.perPage ?? 10
  }), [searchMeta]);

  return {
    // 데이터
    summary,
    donors,
    hasData,
    
    // 상태
    isLoading,
    searchStatus,
    
    // 페이지네이션
    pagination,
    
    // 액션
    fetchDonors,
    
    // 프로젝트 정보
    projectId
  };
};

export default useDonorManagement;

